import React, { useRef, useEffect, useCallback, useState } from "react";

// Global handler registry to work around prop passing issues
export const clickHandlers = new Map();

// Helper function to register a handler by name
export const registerUserClickHandler = (userName, handler) => {
  if (userName && handler && typeof handler === "function") {
    // Normalize the name for consistent lookup
    const normalizedName = userName.trim().replace(/\s+/g, " ");
    clickHandlers.set(normalizedName, handler);
    // Also register with original name in case of exact match
    clickHandlers.set(userName, handler);
  } else {
  }
};

const UnderlineUser = (props) => {
  const { name, onClick, disabled } = props;
  const spanRef = useRef(null);
  const handlerIdRef = useRef(null);
  const [isClickable, setIsClickable] = useState(typeof onClick === "function");
  const clickable = typeof onClick === "function";

  useEffect(() => {
    // Update clickable state when onClick prop changes
    setIsClickable(typeof onClick === "function");
  }, [name, onClick, props]);

  useEffect(() => {
    const element = spanRef.current;
    if (element && name) {
      // Generate a unique ID for this instance
      if (!handlerIdRef.current) {
        handlerIdRef.current = `underline-user-${Date.now()}-${Math.random()}`;
      }

      // Normalize the name for consistent lookup (trim and normalize spaces)
      const normalizedName = name.trim().replace(/\s+/g, " ");

      // Function to check for handler and update state
      const checkForHandler = () => {
        // Check if handler already exists in registry (registered by DocumentHeader)
        const existingHandler =
          clickHandlers.get(normalizedName) || clickHandlers.get(name);
        const effectiveHandler = onClick || existingHandler;
        const isActuallyClickable =
          effectiveHandler && typeof effectiveHandler === "function";

        // Update clickable state if handler is available
        setIsClickable(isActuallyClickable);
        return effectiveHandler;
      };

      // Check immediately
      const effectiveHandler = checkForHandler();

      // Also set up a periodic check in case handler is registered later
      // This helps when handlers are registered after component mounts
      const checkInterval = setInterval(() => {
        const handler = checkForHandler();
        // If we found a handler, we can stop checking
        if (handler && typeof handler === "function") {
          clearInterval(checkInterval);
        }
      }, 100); // Check every 100ms

      // Stop checking after 5 seconds (handlers should be registered by then)
      const timeout = setTimeout(() => {
        clearInterval(checkInterval);
      }, 5000);

      // Store the handler in the global registry by both ID and name
      if (effectiveHandler && typeof effectiveHandler === "function") {
        clickHandlers.set(handlerIdRef.current, effectiveHandler);
        clickHandlers.set(normalizedName, effectiveHandler);
        clickHandlers.set(name, effectiveHandler); // Also store with original name
        element.setAttribute("data-handler-id", handlerIdRef.current);
        element.setAttribute("data-user-name", normalizedName);
      } else {
      }

      const handleClick = (e) => {
        const normalizedName = name.trim().replace(/\s+/g, " ");
        const handlerId = element.getAttribute("data-handler-id");

        e.preventDefault();
        e.stopPropagation();

        // Try multiple lookup methods:
        // 1. By handler ID
        let storedHandler = handlerId ? clickHandlers.get(handlerId) : null;

        // 2. By normalized user name
        if (!storedHandler) {
          storedHandler = clickHandlers.get(normalizedName);
        }

        // 3. Try original name as last resort
        if (!storedHandler) {
          storedHandler = clickHandlers.get(name);
        }

        if (storedHandler && typeof storedHandler === "function") {
          storedHandler(e);
        } else if (onClick && typeof onClick === "function") {
          onClick(e);
        } else {
        }
      };

      element.addEventListener("click", handleClick, true);
      return () => {
        clearInterval(checkInterval);
        clearTimeout(timeout);
        element.removeEventListener("click", handleClick, true);
        if (handlerIdRef.current) {
          clickHandlers.delete(handlerIdRef.current);
        }
      };
    }
  }, [name, clickable, onClick]);

  const handleMouseDown = (e) => {
    if (clickable) {
      e.stopPropagation();
    }
  };

  if (!name) {
    return null;
  }

  // Compute clickability in render to ensure it's always accurate
  // Check both onClick prop and registry for handler - this ensures cursor is always correct
  const normalizedName = name.trim().replace(/\s+/g, " ");
  const registryHandler = clickHandlers.get(normalizedName) || clickHandlers.get(name);
  const isActuallyClickable = (onClick && typeof onClick === "function") || (registryHandler && typeof registryHandler === "function");

  return (
    <span style={{ display: "inline" }}>
      {" "}
      -{" "}
      <span
        ref={spanRef}
        role={isActuallyClickable ? "button" : undefined}
        tabIndex={isActuallyClickable ? 0 : undefined}
        style={{
          textDecoration: "underline",
          cursor: isActuallyClickable && (disabled !== true && disabled !== undefined) ? "pointer" : "default",
          pointerEvents: "auto",
          position: "relative",
          zIndex: isActuallyClickable ? 999 : "auto",
          userSelect: "none",
          WebkitUserSelect: "none",
          MozUserSelect: "none",
          msUserSelect: "none",
        }}
        onClick={(e) => {
          if(disabled){
            return;
          }
          if (isActuallyClickable) {
            const handler = onClick || registryHandler;
            if (handler && typeof handler === "function") {
              e.preventDefault();
              e.stopPropagation();
              handler(e);
            }
          }
        }}
        onMouseDown={handleMouseDown}
        onKeyDown={(e) => {
          if (isActuallyClickable && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            const handler = onClick || registryHandler;
            if (handler && typeof handler === "function") {
              handler(e);
            }
          }
        }}
      >
        {name}
      </span>
    </span>
  );
};

export default UnderlineUser;
